<script lang="ts" setup>
import type InputMaskExpose from '@/objects/InputMaskExpose';
import type InputTextBaseExpose from '@/objects/InputTextBaseExpose';
import { Delete, deleteKeys, noninputkeys } from '@/utilities/keyboardutils';
import { ref, watch } from 'vue';
import InputTextBase from './InputTextBase.vue';
import { type IInputMaskModelValueType, type IInputMaskPropsWithoutModelValue } from '@/components';

const props = defineProps<IInputMaskPropsWithoutModelValue>();

const modelValue = defineModel<IInputMaskModelValueType>({ required: true });

const emits = defineEmits(['maskValueChanged', 'update:modelValue']);

// Set whatever we have in the modelvalue as the current value
props.maskData.setStringValue(modelValue.value);

// When we change the modelvalue, update the mask
watch(
    () => modelValue.value,
    (newVal) => {
        // Set the new value in the mask
        props.maskData.setStringValue(newVal);

        emitChanged();
    },
);

const emitChanged = () => {
    emits('maskValueChanged', props.maskData.getCurrentDisplayText());

    if (props.maskData.isMaskFilled()) {
        // Sigh: If we are using a defineModel, we dont emit the modelValue if the value does not change when we set the modelValue.value property.
        // The InputDateTime depends on the modelValue being emitted back out when we set it.
        // This behaviour differs from using props+emit, that will always emit. So its not the same
        // For not we will always emit, but this should have a cleaner solution
        // The problem probably comes from watching the modelValue, thats the weird thing here
        emits('update:modelValue', props.maskData.getCurrentDisplayText());
        // This isnt strictly necessary, since the emit above will update the modelValue through the binding,
        // but we want a reference to modelValue here.
        modelValue.value = props.maskData.getCurrentDisplayText();
    }
};

const keyDown = (payload: KeyboardEvent) => {
    const key = payload.key;
    if (!key || props.readonly || props.disabled) {
        return; // This can probably never happen
    }

    if (key.length > 1 && !deleteKeys.includes(key)) {
        return; // Easy way to skip Ctrl, Shift and so on.
    }

    // We return on ctrl so ctrl + a, ctrl + c, ctrl + v and so on will work as intended
    if (payload.ctrlKey) {
        return;
    }

    if (noninputkeys.includes(key)) {
        return;
    }

    // We will control the text of the box ourselves so dont allow any input (we do this here so shift/ctrl/whatever dont get blocked)
    payload.preventDefault();

    const input = payload.target as HTMLInputElement;
    const caretIndex = input.selectionStart || 0;

    const selectionEnd = input.selectionEnd || caretIndex;

    let newCaretIndex = caretIndex;
    // User has made a selection, and is pressing a delete key, we just delete everything selected
    if (selectionEnd > caretIndex && deleteKeys.includes(key)) {
        Array.from({ length: selectionEnd - caretIndex }, (_, i) => i + caretIndex).forEach((i) => props.maskData.handleInput(Delete, i));
    } else {
        // Handle input normally
        newCaretIndex = props.maskData.handleInput(key, caretIndex);
    }

    const prevVal = input.value;

    input.value = props.maskData.getCurrentTextForInputElement();

    setCaretPosition(newCaretIndex, input);

    // If the value has changed, notify parent
    if (prevVal != input.value) {
        emitChanged();
    }
};

const paste = (payload: ClipboardEvent) => {
    if (!payload.clipboardData) {
        return;
    }
    const pasted = payload.clipboardData?.getData('text');
    if (!pasted) {
        return;
    }

    // Stop normal handling
    payload.preventDefault();

    const input = payload.target as HTMLInputElement;

    // Try to set the pasted value
    const newCaretIndex = props.maskData.setStringValueAtPosition(pasted, input.selectionStart || 0);

    input.value = props.maskData.getCurrentTextForInputElement();

    setCaretPosition(newCaretIndex, input);

    emitChanged();
};

const focused = (payload: Event) => {
    if (!(payload.target instanceof HTMLInputElement)) {
        return;
    }

    // If the mask is filled we allow the user to set the cursor anywhere
    if (props.maskData.isMaskFilled()) {
        return;
    }

    // Set focus at last filled thing in the mask
    setCaretPosition(props.maskData.getFirstUnfilledIndex(), payload.target);
};

const setCaretPosition = (position: number, element: HTMLInputElement) => {
    element.setSelectionRange(position, position);
};

const htmlinput = ref<InputTextBaseExpose | null>(null);

const focus = () => {
    htmlinput.value?.focus(props.focusKeepsCursorPosition ? false : true);
};

const isFocused = () => htmlinput.value!.isFocused();

defineExpose<InputMaskExpose>({
    focus,
    isFocused,
    KeyboardEvent: (ev: KeyboardEvent) => {
        if (htmlinput.value?.KeyboardEvent) {
            htmlinput.value?.KeyboardEvent(ev);
        }
    },
});
</script>

<template>
    <div
        class="form-control-maskinput flex-nowrap position-relative"
        :class="[$attrs.class]"
    >
        <InputTextBase
            ref="htmlinput"
            v-bind:model-value="maskData.getCurrentTextForInputElement()"
            v-bind="$attrs"
            @keydown="keyDown"
            @focus="focused"
            @paste="paste"
            :autofocus="props.autofocus"
            :disabled="props.disabled"
            :readonly="props.readonly"
            :placeholder="props.placeholder"
            :maxlength="props.maxlength"
            :icon="props.icon"
            :icon-family="props.iconFamily"
            :label="props.label"
            :labelPosition="props.labelPosition"
            :id="props.id"
            type="text"
        />
        <span class="mask text-nowrap">{{ maskData.getGrayMaskValue() }}</span>
    </div>
</template>
